import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { Button } from "./ui";
import { Icon } from "./Icon";
import { micro, ui } from "../motion";
import { haptic } from "../telegram/haptics";
import { useT } from "../i18n/strings";
import s from "./ConfirmDialog.module.css";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body?: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const t = useT();

  /*
   * A dialog that asks whether you meant it should announce itself. Only the
   * transition into open counts — the component stays mounted alongside the
   * record it guards.
   */
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current === open) return;
    wasOpen.current = open;
    if (open) haptic.warning();
  }, [open]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className={s.backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={micro}
          onClick={onCancel}
        >
          <motion.div
            className={s.dialog}
            role="alertdialog"
            aria-modal="true"
            aria-label={title}
            initial={{ opacity: 0, scale: 0.94, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 4 }}
            transition={ui}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={s.mark}>
              <Icon name="delete" size={24} />
            </div>
            <p className={s.title}>{title}</p>
            {body && <p className={s.body}>{body}</p>}
            <div className={s.actions}>
              <Button variant="ghost" onClick={onCancel}>
                {t.actions.cancel}
              </Button>
              {/* Heavier than its cancel on purpose: a confirm that feels the
                  same as backing out is a confirm nobody reads. */}
              <Button variant="destructive" feedback="commit" onClick={onConfirm}>
                {confirmLabel ?? t.actions.delete}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
